import { AlertBulkActions } from '@/components/Studies/Create/Steps/Alerts/Alerts';
import { AlertsTable } from '@/components/Studies/Create/Steps/Alerts/AlertsTable';
import Button from '@/components/UI/Button';
import { errorToast } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { StudyAlertV1, StudyAlertCreateV1, StudyAlertUpdateV1 } from '@/model/Alert.model';
import type { Study } from '@/model/Study.model';
import type { FC } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { modalAction } from '@/utils/modal';
import { RouteService } from '@/support/RouteService';

interface AlertsSettingsProps {
  study: Study;
  alerts: Array<StudyAlertV1>;
  reloadAlerts: () => Promise<void>;
}

const buildAlertPayload = (alert: StudyAlertV1) => ({
  calculation: alert.calculation,
  value: alert.value,
  operator: alert.operator,
  type: alert.type,
  from: alert.from,
  notification: alert.notification,
  retrigger: alert.retrigger,
  action: alert.action,
});

export const AlertsSettings: FC<AlertsSettingsProps> = ({ alerts, study, reloadAlerts }) => {
  const { openModal, closeModal } = modalAction(useDispatch());
  const [selectedRows, onSelectedRowsChange] = useState<Record<number, boolean>>({});
  const calculations = study?.settings?.calculations ?? [];
  const disabled = _isEmpty(calculations) || _notNil(study?.archived_at) || _notNil(study.canceled_at);

  const { invoke: createAlert } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/alerts',
    invokeOnInit: false,
  });

  const { invoke: updateAlert } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/alerts/{studyAlertApiId}',
    invokeOnInit: false,
  });

  const { invoke: bulkDelete } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/alerts/bulk-delete',
    invokeOnInit: false,
  });

  const handleAddAlert = async (alert: StudyAlertV1) => {
    try {
      const payload = buildAlertPayload(alert);
      if (_isNotEmpty(alert.api_id)) {
        const updateBody: StudyAlertUpdateV1 = {
          ...payload,
          name: alert?.name ?? '',
        };
        await updateAlert({
          path: { studyApiId: study.api_id, studyAlertApiId: alert.api_id },
          body: updateBody,
        });
      } else {
        const createBody: StudyAlertCreateV1 = {
          ...payload,
        };
        await createAlert({
          path: { studyApiId: study.api_id },
          body: createBody,
        });
      }
      closeModal();
      await reloadAlerts();
    } catch {
      errorToast(_isNotEmpty(alert.api_id) ? 'Could not update alert' : 'Could not create alert');
    }
  };

  const handleDeleteAlert = async () => {
    const alertIdsToDelete = alerts
      .filter((alert, index) => selectedRows?.[index] ?? false)
      .map(({ api_id }) => api_id);

    try {
      await bulkDelete({
        path: { studyApiId: study.api_id },
        body: alertIdsToDelete,
      });
      await reloadAlerts();
    } catch {
      errorToast(`Could not delete alert${alertIdsToDelete.length > 1 ? 's' : ''}`);
    }
  };

  const addOrUpdateAlert = (alert?: StudyAlertV1) => {
    openModal('ADD_ALERT_FORM', {
      calculations,
      alert,
      onSubmit: handleAddAlert,
      onCancel: () => closeModal(),
    });
  };

  return (
    <>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">Alerts</h3>
        <p className="f6 lh-copy ma0">
          Alerts are rules that can be set to alert users to a status change in an animal.{' '}
          <a
            href={RouteService.help('study_alerts')}
            target="_blank"
            rel="noopener noreferrer"
            className="dib link blue"
          >
            Read more.
          </a>
        </p>
      </div>
      <div className="ui-card">
        <div className="w-100 flex justify-between mt3">
          <AlertBulkActions selectedRows={selectedRows} handleDelete={handleDeleteAlert} />
          <Button
            className="relative mr3 br-pill"
            icon="add_new"
            paleBlue
            onClick={() => addOrUpdateAlert()}
            disabled={disabled}
          >
            <span>Add Alert</span>
          </Button>
        </div>
        <div>
          <AlertsTable
            alerts={alerts}
            disabled={disabled}
            calculations={calculations}
            onAlertClick={(alert) => addOrUpdateAlert(alert)}
            onAddAlertClick={() => addOrUpdateAlert()}
            selectedRows={selectedRows}
            onSelectedRowsChange={onSelectedRowsChange}
          />
        </div>
      </div>
    </>
  );
};
